import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Icon, IconButton, TextField, Tooltip } from '@mui/material';

import { InfraPlan } from '../../models/plan/plan';
import { ListParams } from '../../models/listParams';
import { MijnOrganisatieListItem } from '../../models/user/mijnOrganisatieListItem';

import ProgressBar from '../controls/ProgressBar';
import TablePlannen from '../controls/tables/TablePlannen';
import NoActiveOrganisatieMessage from '../controls/NoActiveOrganisatieMessage';
import PlanDetail from './PlanDetail';
import FilterPlannen from './FilterPlannen';
import DashboardOptionDial from './DashboardOptionDial';

import { eventBus } from '../../services/event-emitter.service';
import { mapService } from '../../services/map.service';
import { userManagementService } from '../../services/user-management.service';
import { plannenService } from '../../services/plannen.service';
import { loadJsonFromLocalStorage, saveJsonToLocalStorage } from '../../services/utils/localStorageState';
import { LOCAL_STORAGE_KEYS, pageSizeStorageKey } from '../../services/utils/localStorageKeys';
import { usePersistedPageSize } from '../../hooks/usePersistedPageSize';

import dayjs from "dayjs";
import ErrorMessage from '../controls/ErrorMessage';

const SEARCH_DEBOUNCE_MS = 450;

const DEFAULT_SELECTED_FILTERS: Record<string, any[]> = {
  status: [],
  discipline: [],
  type: [],
  organisatie: [],
  beoordeling: [],
  gemeente: [],
};

interface StoredPlannenFilterState {
  selectedSearch: string;
  startDateFrom: string;
  startDateTo: string;
  endDateFrom: string;
  endDateTo: string;
  includeZonderEinddatum: boolean;
  selectedFilters: Record<string, any[]>;
  sortBy: string;
  sortDirection: string;
}

const normalizeSelectedFilters = (input: any): Record<string, any[]> => {
  return {
    status: Array.isArray(input?.status) ? input.status : [],
    discipline: Array.isArray(input?.discipline) ? input.discipline : [],
    type: Array.isArray(input?.type) ? input.type : [],
    organisatie: Array.isArray(input?.organisatie) ? input.organisatie : [],
    beoordeling: Array.isArray(input?.beoordeling) ? input.beoordeling : [],
    gemeente: Array.isArray(input?.gemeente) ? input.gemeente : [],
  };
};

const getStoredPlannenFilterState = (): StoredPlannenFilterState => {
  const fallback: StoredPlannenFilterState = {
    selectedSearch: '',
    startDateFrom: '',
    startDateTo: '',
    endDateFrom: '',
    endDateTo: '',
    includeZonderEinddatum: false,
    selectedFilters: DEFAULT_SELECTED_FILTERS,
    sortBy: '',
    sortDirection: '',
  };

  const hasNewKey = typeof window !== 'undefined' && localStorage.getItem(LOCAL_STORAGE_KEYS.PLANNEN_FILTERS) !== null;
  const storageKey = hasNewKey ? LOCAL_STORAGE_KEYS.PLANNEN_FILTERS : 'plannen.filters.v1';

  return loadJsonFromLocalStorage<StoredPlannenFilterState>(
    storageKey,
    fallback,
    (parsed) => {
    return {
      selectedSearch: typeof parsed?.selectedSearch === 'string' ? parsed.selectedSearch : '',
      startDateFrom: typeof parsed?.startDateFrom === 'string' ? parsed.startDateFrom : '',
      startDateTo: typeof parsed?.startDateTo === 'string' ? parsed.startDateTo : '',
      endDateFrom: typeof parsed?.endDateFrom === 'string' ? parsed.endDateFrom : '',
      endDateTo: typeof parsed?.endDateTo === 'string' ? parsed.endDateTo : '',
      includeZonderEinddatum: Boolean(parsed?.includeZonderEinddatum),
      selectedFilters: normalizeSelectedFilters(parsed?.selectedFilters),
      sortBy: typeof parsed?.sortBy === 'string' ? parsed.sortBy : '',
      sortDirection: typeof parsed?.sortDirection === 'string' ? parsed.sortDirection : '',
    };
  });
};


interface SidebarProps {
  panelWidth: number;
  sidebarIsLeft: boolean;
  sizes: number[];
  onSetSizes: (sizes: number[]) => void;
  setSidebarLeft: (isLeft: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  panelWidth,
  sidebarIsLeft,
  sizes,
  onSetSizes,
  setSidebarLeft,
}) => {
  const navigate = useNavigate();
  const initialFilterStateRef = useRef<StoredPlannenFilterState>(getStoredPlannenFilterState());
  
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [pageIndex, setPageIndex] = useState(0);
  const { pageSize, setPageSize } = usePersistedPageSize(pageSizeStorageKey('plannen'), 25);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState<string>(initialFilterStateRef.current.sortBy);
  const [sortDirection, setSortDirection] = useState<string>(initialFilterStateRef.current.sortDirection);
  const [plans, setPlans] = useState<InfraPlan[]>([]);
  const [mijnOrganisatie, setMijnOrganisatie] = useState<MijnOrganisatieListItem | null>(null);

  const [filterData, setFilterData] = useState<any[]>([]);

  const [selectedSearch, setSelectedSearch] = useState<string>(initialFilterStateRef.current.selectedSearch);
  const [startDateFrom, setStartDateFrom] = useState<string>(initialFilterStateRef.current.startDateFrom);
  const [startDateTo, setStartDateTo] = useState<string>(initialFilterStateRef.current.startDateTo);
  const [endDateFrom, setEndDateFrom] = useState<string>(initialFilterStateRef.current.endDateFrom);
  const [endDateTo, setEndDateTo] = useState<string>(initialFilterStateRef.current.endDateTo);
  const [includeZonderEinddatum, setIncludeZonderEinddatum] = useState<boolean>(initialFilterStateRef.current.includeZonderEinddatum);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, any[]>>(initialFilterStateRef.current.selectedFilters);
  const [ready, setReady] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [filterPanelOpen, setFilterPanelOpen] = useState<boolean>(false);

  const { id: routePlanId } = useParams(); // haalt route plan id op

  const handleClickPlan = useCallback((id: string) => {
    if (id !== routePlanId) {
      navigate(`/plannen/${id}`);
    }
  }, [navigate, routePlanId]);

  useEffect(() => {
    eventBus.on('slideInPlan', handleClickPlan);
    return () => {
      eventBus.off('slideInPlan', handleClickPlan);
    };
  }, [handleClickPlan]);

  useEffect(() => {
    const stateToStore: StoredPlannenFilterState = {
      selectedSearch,
      startDateFrom,
      startDateTo,
      endDateFrom,
      endDateTo,
      includeZonderEinddatum,
      selectedFilters: normalizeSelectedFilters(selectedFilters),
      sortBy,
      sortDirection,
    };

    saveJsonToLocalStorage(LOCAL_STORAGE_KEYS.PLANNEN_FILTERS, stateToStore);
  }, [selectedSearch, startDateFrom, startDateTo, endDateFrom, endDateTo, includeZonderEinddatum, selectedFilters, sortBy, sortDirection]);

  // Ref guard: persists through StrictMode's cleanup/remount, so fetchFilterData only fires once.
  const filterFetchStartedRef = useRef(false);

  // Initialized to the stored initial value so that the initial ready→true transition is skipped
  const prevSelectedSearchRef = useRef<string>(initialFilterStateRef.current.selectedSearch);

  // Only fire debounce when selectedSearch actually changes after the component is ready.
  // Using ready as guard + prev-value comparison prevents StrictMode double-invoke issues.
  useEffect(() => {
    if (!ready) return;
    if (prevSelectedSearchRef.current === selectedSearch) return;
    prevSelectedSearchRef.current = selectedSearch;

    const timeoutId = window.setTimeout(() => {
      setPageIndex(0);
      refetchPlans();
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [ready, selectedSearch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!ready) return;
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, refreshKey]);

  useEffect(() => { setReady(true); }, []);

  useEffect(() => {
    if (filterFetchStartedRef.current) return;
    filterFetchStartedRef.current = true;
    fetchFilterData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetchPlans = () => {
    setRefreshKey(k => k + 1);
  };

  /* Function to format the filters for the API request */
  const formatFilters = (filters: Record<string, any[]>): string => {
    const formattedFilters = Object.entries(filters)
      .filter(([_, values]) => values.length > 0) // Remove empty filters
      .map(([filterType, values]) => {
        let type = '';
        switch (filterType) {
          case 'status':
            type = 'planStatus';
            break;
          case 'type':
            type = 'planType';
            break;
          case 'organisatie':
            type = 'infplOrganizationId';
            break;
          case 'discipline':
            type = 'discipline';
            break;
          case 'beoordeling':
            type = 'infplBeoordeling';
            break;
          case 'gemeente':
            type = 'GemeenteCodes';
            break;
          // case 'kosten':
            // kosten > 100
            // break;
          default:
            type = filterType;
            break;
        }
        const filterValues = values.map((value) => `'${value}'`).join(', ');
        const filterString = `${type} in (${filterValues})`;
        return `${filterString}`;
      })
      .join(' and ');
  
    return formattedFilters;
  };

  const createParamFilter = (): string => {
    const filters: string[] = [];
    const toDateTimeLiteral = (value: string, endOfDay: boolean): string => {
      return dayjs(value).format(endOfDay ? 'YYYY-MM-DDT23:59:59.000[Z]' : 'YYYY-MM-DDT00:00:00.000[Z]');
    };
  
    if (
      selectedFilters && (
        selectedFilters.status?.length > 0 ||
        selectedFilters.discipline?.length > 0 ||
        selectedFilters.type?.length > 0 ||
        selectedFilters.organisatie?.length > 0 ||
        selectedFilters.beoordeling?.length > 0 ||
        selectedFilters.gemeente?.length > 0
      )
    ) {
      const formattedFilter = formatFilters(selectedFilters);
      if (formattedFilter) {
        filters.push(formattedFilter);
      }
    }

    // Startdatum filter
    const hasStartFrom = !!startDateFrom && dayjs(startDateFrom).isValid();
    const hasStartTo = !!startDateTo && dayjs(startDateTo).isValid();
    if (hasStartFrom) {
      filters.push(`planStartDatum >= ${toDateTimeLiteral(startDateFrom, false)}`);
    }
    if (hasStartTo) {
      filters.push(`planStartDatum <= ${toDateTimeLiteral(startDateTo, true)}`);
    }

    // Einddatum filter
    const hasEndFrom = !!endDateFrom && dayjs(endDateFrom).isValid();
    const hasEndTo = !!endDateTo && dayjs(endDateTo).isValid();
    if (hasEndFrom || hasEndTo) {
      const rangeConditions: string[] = [];
      if (hasEndFrom) {
        rangeConditions.push(`planEindDatum >= ${toDateTimeLiteral(endDateFrom, false)}`);
      }
      if (hasEndTo) {
        rangeConditions.push(`planEindDatum <= ${toDateTimeLiteral(endDateTo, true)}`);
      }
      if (includeZonderEinddatum) {
        filters.push(`(planEindDatum = null or ${rangeConditions.join(' and ')})`);
      } 
      else {
        rangeConditions.forEach(c => filters.push(c));
      }
    }

    return filters.join(' and ');
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 2000)); // Bijwerken lijst kan soms wat langer duren...

      const storedOrganisatie = userManagementService.getStoredOrganisatie();
      if (storedOrganisatie && storedOrganisatie.id) {
        setMijnOrganisatie(storedOrganisatie);

        const params: ListParams = {
          searchText: selectedSearch,
          offset: pageIndex * pageSize, 
          limit: pageSize, 
          sortBy: sortBy ? `${sortBy} ${sortDirection}` : "", // Add sorting here
          filter: createParamFilter()
        };
                
        const data = await plannenService.getPlannen(params);
        eventBus.emit('setOgcFilter', params.searchText, params.filter);
        const modifiedDataItems = data.items.map((plan: InfraPlan, index: number) => ({
          ...plan,          // Copy the original plan properties
          id: plan.infplId,  // `plan-${index}`; // Add a unique id based on infplId or index for key
        }));

        if (modifiedDataItems.length > 0) {
          // Get all bbox from plans
          const arrBbox = modifiedDataItems.map((plan: InfraPlan) => plan.bbox);
          const bbox = mapService.calculatedBoundingBox(arrBbox) as number[];
          eventBus.emit('zoomInOnPlanBbox', bbox);
          
          // const bboxRD = [81002.2277319291, 454564.77977588197, 81664.38057003169, 454866.2470151363];
          // const bboxLatLng = mapService.convertBboxRDToLatLng(bboxRD, true) as number[];  
          // console.log('1 Converted to LatLng:', bboxLatLng);
          // const bboxBackToRD = mapService.convertBboxLatLngToRD(bboxLatLng, true) as number[];  
          // console.log('2 Converted back to RD:', bboxBackToRD);
        }
        else {
          eventBus.emit('zoomInOnPlanBbox'); // Zonder bbox behoud de huidige bbox
        }

        setPlans(modifiedDataItems); // Assuming the API response is a JSON array of plans

        setTotalCount(data.count);
        setError(null);
        setLoading(false);
      }
      else {
        setMijnOrganisatie(null);
        setPlans([]);
        setTotalCount(0);
        setLoading(false);
      }
    }
    catch (error: any) {
      // If token refresh failed, redirect to the logout page
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }

      setError('Er is een fout opgetreden');
      setLoading(false);
    }
  };

  const fetchFilterData = async () => {
    try {
      const filters = await plannenService.getPlannenFilters();
      if (filters.discipline || filters.planStatus || filters.planType || filters.infplBeoordeling || filters.organizations) {
        setFilterData(filters);
      }
    }
    catch (error: any) {
      // If token refresh failed, redirect to the logout page
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError('Er is een fout opgetreden');
      setLoading(false);
    }
  };

  const handlePagination = (pageIndex: number, pageSize: number) => {
    setPageIndex(pageIndex);
    setPageSize(pageSize);
    refetchPlans(); // Re-fetch plans when search query changes
  };

  const handleSorting = (field: string, direction: string) => {
    setSortBy(field);
    setSortDirection(direction);
    refetchPlans();
  };

  const handleClosePlan = () => {
    navigate('/plannen');
  };

  const removeFilterAll = async () => {
    setSelectedSearch('');
    setStartDateFrom('');
    setStartDateTo('');
    setEndDateFrom('');
    setEndDateTo('');
    setIncludeZonderEinddatum(false);
    setSelectedFilters(DEFAULT_SELECTED_FILTERS);

    setPageIndex(0);
    refetchPlans(); // Re-fetch plans when search query changes
  };

  const handleFilterChange = (key: string, value: any) => {
    setSelectedFilters((prevFilters) => {
      if (Array.isArray(value)) {
        return { ...prevFilters, [key]: value };
      }

      const currentValues = prevFilters[key] || [];

      const isAlreadySelected = currentValues.includes(value);
      const updatedValues = isAlreadySelected
        ? currentValues.filter((item) => item !== value) // Remove if already selected
        : [...currentValues, value]; // Add if not selected

      return { ...prevFilters, [key]: updatedValues };
    });
    refetchPlans();
  };

  const handleAction = (key: string, value: any, isDelete = false) => {
    switch (key) {
      case 'search':
        setSelectedSearch(isDelete ? '' : value);
        break;
      case 'startDateFrom':
        setStartDateFrom(isDelete ? '' : value);
        refetchPlans();
        break;
      case 'startDateTo':
        setStartDateTo(isDelete ? '' : value);
        refetchPlans();
        break;
      case 'endDateFrom':
        setEndDateFrom(isDelete ? '' : value);
        if (isDelete) setIncludeZonderEinddatum(false);
        refetchPlans();
        break;
      case 'endDateTo':
        setEndDateTo(isDelete ? '' : value);
        if (isDelete) setIncludeZonderEinddatum(false);
        refetchPlans();
        break;
      case 'includeZonderEinddatum':
        setIncludeZonderEinddatum(value);
        refetchPlans();
        break;
      case 'all':
        if (isDelete) {
          removeFilterAll();
        }
        else {
          handleFilterChange(key, value);
        }
        break;
      default:
        handleFilterChange(key, value);
        break;
    }
  };

  const handleChange = (key: string, value: any, isDelete: boolean = false) => {
    handleAction(key, value, isDelete);
  };

  const activeFilterCount =
    Object.values(selectedFilters).reduce((sum, arr) => sum + arr.length, 0) +
    (startDateFrom ? 1 : 0) +
    (startDateTo ? 1 : 0) +
    (endDateFrom ? 1 : 0) +
    (endDateTo ? 1 : 0) +
    (includeZonderEinddatum ? 1 : 0);

  if (!mijnOrganisatie) {
    return (
      <>
        <NoActiveOrganisatieMessage />  
      </>
    )
  }

  return (
    <>
      <div className="sidebar sidebar-with-filter-panel">

        <div className={`filter-panel-left${filterPanelOpen ? '' : ' filter-panel-left--closed'}`}>
          <FilterPlannen
            sidePanel={true}
            onClose={() => setFilterPanelOpen(false)}
            onChange={handleChange}
            filterData={filterData}
            selectedSearch={selectedSearch}
            startDateFrom={startDateFrom}
            startDateTo={startDateTo}
            endDateFrom={endDateFrom}
            endDateTo={endDateTo}
            includeZonderEinddatum={includeZonderEinddatum}
            selectedFilters={selectedFilters}
          />
        </div>

        <div className="plans-content-area">
          <div className="sidebar-search-bar">
            <TextField
              id="search"
              label="Zoeken in plannen"
              variant="filled"
              placeholder="Zoeken op nummer, naam, status, beoordeling, type, discipline en organisatie"
              value={selectedSearch ?? ''}
              onChange={(e) => handleChange('search', e.target.value)}
              InputProps={{
                endAdornment: selectedSearch ? (
                  <IconButton size="small" onClick={() => handleChange('search', '')}>
                    <Icon>clear</Icon>
                  </IconButton>
                ) : null,
              }}
            />
            <Tooltip
                title={activeFilterCount > 0 ? `${activeFilterCount} filter${activeFilterCount !== 1 ? 's' : ''} actief` : ''}
                disableHoverListener={activeFilterCount === 0}
              >
              <Badge
                badgeContent={activeFilterCount}
                color="primary"
                overlap="rectangular"
                sx={{ '& .MuiBadge-badge': { top: 6, right: 6 } }}
              >
                <Tooltip title={filterPanelOpen ? 'Sluit het filter' : 'Open filter om uitgebreid te zoeken in de plannen'}>
                  <IconButton size="large" onClick={() => setFilterPanelOpen(!filterPanelOpen)}>
                    <Icon>{filterPanelOpen ? 'filter_list_off' : 'filter_list'}</Icon>
                  </IconButton>
                </Tooltip>
              </Badge>
            </Tooltip>
          </div>

          {/* <FilterOverview
            search={selectedSearch}
            startDate={selectedStartDate}
            endDate={selectedEndDate}
            matchExactRange={matchExactRange}
            filterData={filterData}
            filters={selectedFilters}
            onDelete={(key: string, value: string) => handleDelete(key, value)}
          /> */}
          {error && <ErrorMessage message={error} />}
          <TablePlannen
            plans={plans}
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalCount={totalCount}
            panelWidth={panelWidth}
            mijnOrganisatie={mijnOrganisatie}
            onPagination={handlePagination}
            onSorting={handleSorting}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onClickPlan={handleClickPlan}
          />
          <DashboardOptionDial
            sidebarIsLeft={sidebarIsLeft}
            sizes={sizes}
            onSetSizes={onSetSizes}
            setSidebarLeft={setSidebarLeft}
          />
        </div>

        <div className={`plan-detail ${routePlanId ? "active" : ""}`}>
          { routePlanId && 
            <PlanDetail 
              planId={routePlanId}
              enableZoomInOnPlanBbox={true}
              onClosePlan={handleClosePlan}
              refetchPlannen={refetchPlans}
            />
          }
        </div>
      </div>
      
      { loading && (
        <ProgressBar text="Een moment geduld a.u.b. " />
      )}
      
    </>
  );
};

export default Sidebar;